import { memo, useMemo } from 'react';

type FormProgressBarProps = {
  /** Number of segments rendered for the flow. */
  totalSteps: number
  /** 1-based step the user is currently on; that segment and the ones before it render filled. */
  currentStep: number
  /** Mobile bars span the full modal width, desktop bars match the 11/12 form width. */
  variant?: 'desktop' | 'mobile'
  /** Tailwind background class used for completed segments. */
  fillClassName?: string
  /** Extra classes appended to the bar wrapper. */
  className?: string
}

/**
 * Resolves the 1-based step to highlight from per-step completion flags:
 * the first incomplete step, or the final step once everything is complete.
 */
export const getActiveFormStep = (stepsCompleted: boolean[]): number => {
  const firstIncomplete = stepsCompleted.findIndex((isComplete) => !isComplete);
  return firstIncomplete === -1 ? Math.max(stepsCompleted.length, 1) : firstIncomplete + 1;
};

/**
 * Segmented step indicator shared by the resource request forms, mirroring the
 * kitchen booking progress bar (`BOOKING_PROGRESS_TOTAL` in `RequestKitchen`).
 */
const FormProgressBar = memo(({
  totalSteps,
  currentStep,
  variant = 'desktop',
  fillClassName = 'bg-primaryDark-400',
  className = '',
}: FormProgressBarProps) => {
  const segmentCount = Math.max(Math.trunc(totalSteps) || 0, 1);
  const filledCount = Math.min(Math.max(Math.trunc(currentStep) || 0, 0), segmentCount);
  const segments = useMemo(() => Array.from({ length: segmentCount }, (_, index) => index), [segmentCount]);

  return (
    <div
      className={`flex flex-row h-[4px] border-0 ${variant === 'mobile' ? 'w-full' : 'w-11/12'} my-4 mx-auto justify-start gap-2 ${className}`}
      data-testid="form-progress-bar"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={segmentCount}
      aria-valuenow={filledCount}
    >
      {segments.map((index) => (
        <div
          key={index}
          className={`h-[4px] border-0 flex-1 rounded-full ${index < filledCount ? fillClassName : 'bg-gray-200'}`}
        />
      ))}
    </div>
  );
});

FormProgressBar.displayName = 'FormProgressBar';

export default FormProgressBar;
